<template>
  <div class="banner" :style="{ backgroundImage: `url(${imageurl})` }">
    <div class="banner-content scale-up">
      <!-- animate.css 的类名通常用于触发入场动画，这里保持 -->
      <div class="animate__animated animate__flipInX">
        <h1></h1>
        <p></p>
        <p><span class="constantia-font ignore"></span></p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import frontServer from '../../../services/FrontManage'; // 路径保持不变

const backendBaseUrl = process.env.VUE_APP_BACKEND_BASE_URL;
const imageurl = ref('');

onMounted(async () => {
  try {
    const baseConfig = await frontServer.GetFront();
    if (baseConfig && baseConfig.mainImage) {
      imageurl.value = `${backendBaseUrl}/${baseConfig.mainImage}`;
    } else {
      // 可以设置一个默认的背景图片URL，以防API没有返回图片
      imageurl.value = 'https://via.placeholder.com/1920x1080?text=Default+Banner';
      console.warn('Banner mainImage not found, using default.');
    }
  } catch (error) {
    console.error('Failed to load banner image:', error);
    imageurl.value = 'https://via.placeholder.com/1920x1080?text=Error+Loading+Banner';
  }
});
</script>

<style scoped>
.banner {
  height: 100vh;
  min-height: 500px; /* 确保在小屏幕上也有最小高度 */
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden; /* 防止内容溢出 */
}

.banner-content {
  max-width: 800px;
  padding: 0 20px;
  /* 性能优化 */
  will-change: transform, opacity;
  backface-visibility: hidden;
}

.banner-content.scale-up {
  /* 桌面端的默认缩放和平移 */
  transform: scale(1.25) translateY(-150px);
  transform-origin: center;
}

.banner-content h1 {
  font-size: 3rem; /* 桌面端字体大小 */
  margin-bottom: 1rem;
  font-family: 'Microsoft JhengHei', sans-serif;
  font-weight: 900;
  letter-spacing: 2px;
  color: #f046b2; /* 霓虹粉文字 */
  text-shadow:
    0 0 2px #fff,       /* 細白邊緣，提高清晰度 */
    0 0 8px #FF6EC7,
    0 0 16px #FF6EC7,
    0 0 32px #FF6EC7,
    0 0 48px #FF1493;   /* 深粉外延，增強層次 */
  animation: text-glow 2s ease-in-out infinite alternate;
}

.banner-content p {
  font-size: 1.5rem; /* 桌面端字体大小 */
  margin-bottom: 1.5rem; /* 调整了间距 */
  font-family: 'Constantia', 'Arial', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
  font-weight: 700;
  line-height: 1.6;
  letter-spacing: 1px;
  color: #FFFFFF; /* 白色字體 */
  text-shadow:
    0 0 8px #00BFFF,
    0 0 16px #00BFFF,
    0 0 24px #00BFFF; /* 加強發光層次 */
  animation: sub-glow 2s ease-in-out infinite alternate;
}
.constantia-font {
  font-family: 'Constantia', serif;
}

@keyframes text-glow {
  from {
    text-shadow:
      0 0 2px #ffffff80, /* 调整透明度以适应不同背景 */
      0 0 8px #FF6EC7,
      0 0 16px #FF6EC7,
      0 0 32px #FF6EC7;
  }
  to {
    text-shadow:
      0 0 4px #ffffff80,
      0 0 12px #FF6EC7,
      0 0 24px #FF6EC7,
      0 0 40px #FF1493;
  }
}

@keyframes sub-glow {
  from {
    opacity: 0.9; /* 稍微降低初始不透明度 */
    text-shadow:
      0 0 6px #00BFFF, /* 调整阴影大小 */
      0 0 12px #00BFFF,
      0 0 18px #00BFFF;
  }
  to {
    opacity: 1;
    text-shadow:
      0 0 10px #00BFFF,
      0 0 18px #00BFFF,
      0 0 26px #00BFFF;
  }
}

/* 响应式调整 */

/* 中等屏幕 (例如平板) */
@media (max-width: 992px) {
  .banner-content.scale-up {
    transform: scale(1.1) translateY(-100px); /* 减小缩放和平移幅度 */
  }
  .banner-content h1 {
    font-size: 2.5rem; /* 减小标题字体 */
  }
  .banner-content p {
    font-size: 1.25rem; /* 减小段落字体 */
    margin-bottom: 1.25rem;
  }
}

/* 小型屏幕 (例如手机) */
@media (max-width: 768px) {
  .banner-content.scale-up {
    transform: scale(1) translateY(-60px); /* 进一步减小或移除缩放，减小平移 */
  }
  .banner-content h1 {
    font-size: 2rem; /* 进一步减小标题字体 */
    letter-spacing: 1px; /* 减小字间距 */
    margin-bottom: 0.8rem;
  }
  .banner-content p {
    font-size: 1.1rem; /* 进一步减小段落字体 */
    margin-bottom: 1rem;
    line-height: 1.5;
  }
  .banner {
    align-items: flex-start; /* 让内容更靠上一些，给小屏幕更多垂直空间 */
    padding-top: 25vh; /* 调整垂直对齐的起始点 */
  }
  .banner-content {
    max-width: 90%; /* 允许内容占据更大宽度比例 */
  }
}

/* 超小型屏幕 */
@media (max-width: 480px) {
  .banner-content.scale-up {
    transform: scale(0.9) translateY(-40px); /* 甚至可以稍微缩小，调整平移 */
  }
  .banner-content h1 {
    font-size: 1.6rem;
  }
  .banner-content p {
    font-size: 0.95rem;
    margin-bottom: 0.8rem;
  }
  .banner {
     padding-top: 20vh;
  }
}

</style>